// Practice days (Matrix Academy): made-up 1-minute days for our own practice instruments, so the
// practice account works without TopstepX and nobody mistakes them for real prices. MX moves and
// pays like ES (MMX like MES), NX like NQ (MNX like MNQ). Each day is one of seven real day types
// (trend, range, opening drive, failed breakout, news spike, V reversal, chop), drawn from a fixed
// seed so a day is the same for everyone, which keeps exams and drills fair.
//
// The shape comes from waypoints (where the day goes, and when), with the price wandering between
// them like a real market: candles are bigger at the open and smaller at lunch, busy stretches come
// in bursts, and turns happen at the levels traders watch (the opening range, the premarket high
// and low, round numbers). Candle sizes come from how ES and NQ trade, or from numbers measured on
// real days when an admin has run the measurement (practice.js, `calibration`).

export const PRACTICE_INSTRUMENTS = {
  MX: { like: 'ES', family: 'ES', name: 'Matrix 500' },
  MMX: { like: 'MES', family: 'ES', name: 'Micro Matrix 500' },
  NX: { like: 'NQ', family: 'NQ', name: 'Matrix 100' },
  MNX: { like: 'MNQ', family: 'NQ', name: 'Micro Matrix 100' },
};
export const PRACTICE_INSTRUMENT_IDS = ['MMX', 'MX', 'MNX', 'NX'];
export const isPracticeInstrument = (symbol) => Boolean(PRACTICE_INSTRUMENTS[symbol]);

export const MINUTES = 465; // 7:30 to 15:15 Chicago, one candle a minute
const SUB = 6; // price points inside each candle
const OPEN = 60; // 8:30 Chicago, the cash open
const BUCKET = 15; // minutes per step of the time-of-day profile

// Average 1-minute candle size (high to low) in each 15 minutes from 7:30 Chicago, in ES points
// at 6,800: quiet premarket, the busy open, the lunch lull and the push into the close.
const ES_POINTS = [
  1.6, 1.2, 1.1, 1.2, // 7:30 premarket
  4.5, 3.5, 3.0, 2.6, // 8:30 the open
  2.4, 2.2, 2.0, 1.9, // 9:30
  1.8, 1.7, 1.5, 1.4, // 10:30
  1.3, 1.2, 1.2, 1.2, // 11:30 lunch
  1.2, 1.3, 1.5, 1.5, // 12:30
  1.6, 1.7, 1.9, 2.0, // 13:30
  2.1, 2.4, 1.5, //      14:30 the close, then after it
];
const FAMILIES = {
  ES: { base: 6800, round: 25, tick: 0.25, profile: ES_POINTS.map((p) => p / 6800), dayRange: 0.0085, volume: 1 },
  NQ: { base: 25000, round: 100, tick: 0.25, profile: ES_POINTS.map((p) => (p / 6800) * 1.3), dayRange: 0.011, volume: 0.45 },
};

// Measured numbers replace the defaults only within sensible bounds.
function familyFor(symbol, calibration) {
  const key = (PRACTICE_INSTRUMENTS[symbol] || PRACTICE_INSTRUMENTS.MX).family;
  const fam = { ...FAMILIES[key], key };
  const m = calibration && calibration[key];
  const near = (v, d) => (Number.isFinite(v) && v > 0 ? Math.min(d * 2.5, Math.max(d * 0.4, v)) : d);
  if (m && Array.isArray(m.profile) && m.profile.length === fam.profile.length) fam.profile = fam.profile.map((d, i) => near(m.profile[i], d));
  if (m) fam.dayRange = near(m.dayRange, fam.dayRange);
  return fam;
}

// A small seeded random generator, so each day comes out the same every time.
function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const gauss = (rand) => Math.sqrt(-2 * Math.log(1 - rand())) * Math.cos(2 * Math.PI * rand());

// --- The day types ------------------------------------------------------------------
// Each is a list of [minute from 7:30, where the price goes], the target worked out from the day
// so far (c): its open, opening range, high and low, and the levels nearby. `d` is the way the day
// goes in the end (1 up, -1 down) and R its usual range in price.

const edge = (c, d) => (d > 0 ? c.or().h : c.or().l);
const ext = (c, d) => (d > 0 ? c.hi : c.lo);

export const DAY_TYPES = {
  trend: {
    scale: 1.15,
    steps: (d) => [
      [75, (c) => c.o + d * c.R * c.r(0.04, 0.12)],
      [100, (c) => edge(c, d) + d * c.R * c.r(0.12, 0.2)], // breaks out of the opening range
      [120, (c) => edge(c, d) + d * c.R * 0.02], // comes back to it, and it holds
      [175, (c) => c.o + d * c.R * c.r(0.45, 0.55)],
      [205, (c) => c.snap(c.last - d * (c.last - c.o) * c.r(0.25, 0.4))], // a pullback to a level
      [300, (c) => ext(c, d) + d * c.R * 0.05], // a new high (low) into lunch
      [340, (c) => c.last - d * c.R * 0.12],
      [430, (c) => c.o + d * c.R * c.r(0.85, 1)],
      [465, (c) => c.last - d * c.R * 0.05],
    ],
  },
  range: {
    scale: 0.85,
    steps: () => [
      [75, (c) => c.o + c.R * c.r(-0.05, 0.05)],
      [95, (c) => {
        // The day's box: the opening range or the premarket high and low, whichever is wider.
        let top = c.snap(Math.max(c.or().h, c.pmH));
        let bottom = c.snap(Math.min(c.or().l, c.pmL));
        const mid = (top + bottom) / 2;
        if (top - bottom < c.R * 0.35) { top = mid + c.R * 0.18; bottom = mid - c.R * 0.18; }
        if (top - bottom > c.R * 0.6) { top = mid + c.R * 0.3; bottom = mid - c.R * 0.3; }
        Object.assign(c.memo, { top, bottom, mid });
        return top + c.R * 0.03; // pokes above the box and fails
      }],
      [140, (c) => c.memo.bottom + c.R * 0.02],
      [190, (c) => c.memo.mid + c.R * c.r(-0.04, 0.04)],
      [240, (c) => c.memo.top - c.R * 0.02],
      [300, (c) => c.memo.bottom - c.R * 0.03], // pokes below and fails
      [360, (c) => c.memo.mid + c.R * c.r(0, 0.08)],
      [410, (c) => c.memo.top - c.R * 0.01],
      [465, (c) => c.memo.mid + c.R * c.r(-0.06, 0.06)],
    ],
  },
  openingDrive: {
    scale: 1.05,
    steps: (d) => [
      [72, (c) => c.o + d * c.R * c.r(0.3, 0.4)], // straight away from the open
      [100, (c) => c.last + d * c.R * c.r(0.15, 0.25)],
      [130, (c) => c.last - d * c.R * 0.12],
      [180, (c) => ext(c, d) + d * c.R * 0.03],
      [260, (c) => c.last - d * c.R * 0.15], // then the rest of the day goes sideways
      [340, (c) => c.last + d * c.R * 0.08],
      [420, (c) => c.last - d * c.R * 0.06],
      [465, (c) => c.last + d * c.R * 0.03],
    ],
  },
  failedBreakout: {
    scale: 1.05,
    steps: (d) => [
      [75, (c) => c.o + c.R * c.r(-0.04, 0.04)],
      [92, (c) => edge(c, -d) - d * c.R * c.r(0.06, 0.1)], // breaks out the wrong way and traps traders
      [108, (c) => c.o],
      [135, (c) => edge(c, d) + d * c.R * 0.1], // then through the other side
      [150, (c) => edge(c, d) + d * c.R * 0.02],
      [220, (c) => c.o + d * c.R * 0.55],
      [260, (c) => c.last - d * c.R * 0.12],
      [380, (c) => ext(c, d) + d * c.R * 0.15],
      [465, (c) => c.last - d * c.R * 0.05],
    ],
  },
  newsSpike: {
    scale: 1.1,
    news: 90, // a 9:00 report
    steps: (d) => [
      [75, (c) => c.o + c.R * c.r(-0.08, 0.08)],
      [89, (c) => c.last + c.R * c.r(-0.03, 0.03)],
      [91, (c) => c.last + d * c.R * c.r(0.3, 0.4)], // the report: a huge candle or two
      [105, (c) => c.last - d * c.R * c.r(0.1, 0.15)], // gives part of it back
      [150, (c) => ext(c, d) + d * c.R * 0.08],
      [240, (c) => c.last - d * c.R * 0.1],
      [360, (c) => c.last + d * c.R * 0.12],
      [465, (c) => c.last - d * c.R * 0.04],
    ],
  },
  vReversal: {
    scale: 1.1,
    steps: (d) => [
      [75, (c) => c.o + d * c.R * c.r(-0.02, 0.06)],
      [140, (c) => c.snap(c.o - d * c.R * c.r(0.45, 0.55))], // sells off (rallies) into a level
      [155, (c) => c.last + d * c.R * 0.1],
      [175, (c) => ext(c, -d) + d * c.R * 0.03], // tests it again and holds
      [250, (c) => c.o - d * c.R * 0.1],
      [330, (c) => c.o + d * c.R * 0.08], // back through the open
      [420, (c) => c.o + d * c.R * 0.3],
      [465, (c) => c.last - d * c.R * 0.04],
    ],
  },
  chop: {
    scale: 0.6,
    calm: 0.85,
    steps: () => [
      [75, (c) => c.o + c.R * c.r(-0.06, 0.06)],
      ...[120, 165, 210, 255, 300, 345, 390, 435, 465].map((m) => [m, (c) => c.o + c.R * c.r(-0.25, 0.25)]),
    ],
  },
};
const TYPE_IDS = Object.keys(DAY_TYPES);

// What each day type was, shown when the day is over.
export const DAY_TYPE_TEXT = {
  trend: { en: 'Trend day', es: 'Día de tendencia', lesson: { en: 'The opening range broke and held. Pullbacks to it, and to levels after it, were the trades with the trend.', es: 'El rango de apertura se rompió y se sostuvo. Los retrocesos hacia él, y hacia los niveles después, eran las operaciones a favor de la tendencia.' } },
  range: { en: 'Range day', es: 'Día de rango', lesson: { en: 'Breakouts failed on both sides. The edges of the box were the places to fade, and the middle was no man’s land.', es: 'Las rupturas fallaron por los dos lados. Los bordes de la caja eran el lugar para ir en contra, y el medio era tierra de nadie.' } },
  openingDrive: { en: 'Opening drive', es: 'Impulso de apertura', lesson: { en: 'The move came in the first hour, then the day went sideways. Chasing after 10 AM only paid the market.', es: 'El movimiento llegó en la primera hora y luego el día se fue de lado. Perseguir el precio después de las 10 AM solo le pagó al mercado.' } },
  failedBreakout: { en: 'Failed breakout', es: 'Ruptura fallida', lesson: { en: 'The first breakout trapped traders and reversed. The real move started when price came back through the opening range.', es: 'La primera ruptura atrapó a los traders y se dio la vuelta. El movimiento real empezó cuando el precio cruzó de nuevo el rango de apertura.' } },
  newsSpike: { en: 'News spike', es: 'Pico por noticia', lesson: { en: 'The 9:00 report moved price in seconds. Waiting for the dust to settle and trading the pullback beat jumping in.', es: 'El reporte de las 9:00 movió el precio en segundos. Esperar a que se calmara y operar el retroceso fue mejor que lanzarse.' } },
  vReversal: { en: 'V reversal', es: 'Reversión en V', lesson: { en: 'The morning move ran into a level, held on a second test, and reversed. Shorting the lows was the trap.', es: 'El movimiento de la mañana llegó a un nivel, se sostuvo en una segunda prueba y se dio la vuelta. Vender en los mínimos fue la trampa.' } },
  chop: { en: 'Chop day', es: 'Día sin dirección', lesson: { en: 'No direction and small candles. The best trade was few trades, or none.', es: 'Sin dirección y con velas pequeñas. La mejor operación fue hacer pocas, o ninguna.' } },
};

// --- The library ----------------------------------------------------------------------
// 28 numbered practice days, four of each type in a mixed order (so the number doesn't give the
// type away), and five fixed exam days.

export const PRACTICE_DAY_COUNT = 28;
const order = (() => {
  const list = TYPE_IDS.flatMap((type) => [type, type, type, type]);
  const rand = rng(2026);
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
})();
const EXAM_TYPES = ['trend', 'failedBreakout', 'range', 'newsSpike', 'vReversal'];
// The 30-day challenge's days (course/capstone.js): every type four times plus two more trend
// days, in a fixed shuffled order, the same for everyone.
const CAPSTONE_ORDER = (() => {
  const list = [...TYPE_IDS.flatMap((type) => [type, type, type, type]), 'trend', 'trend'];
  const rand = rng(3030);
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
})();

// { id, n, type, dir, seed, exam } for 'p1'…'p28', 'exam-1'…'exam-5' or 'cap-1'…'cap-30'; null for
// anything else.
export function practiceDayInfo(id) {
  const p = /^p(\d+)$/.exec(id || '');
  if (p && Number(p[1]) >= 1 && Number(p[1]) <= PRACTICE_DAY_COUNT) {
    const n = Number(p[1]);
    const type = order[n - 1];
    // Half of each type's days go up and half down.
    const before = order.slice(0, n - 1).filter((x) => x === type).length;
    return { id, n, type, dir: before % 2 ? -1 : 1, seed: 1000 + n * 7919, exam: false };
  }
  const e = /^exam-(\d)$/.exec(id || '');
  if (e && Number(e[1]) >= 1 && Number(e[1]) <= EXAM_TYPES.length) {
    const n = Number(e[1]);
    return { id, n, type: EXAM_TYPES[n - 1], dir: n % 2 ? 1 : -1, seed: 50000 + n * 104729, exam: true };
  }
  const c = /^cap-(\d+)$/.exec(id || '');
  if (c && Number(c[1]) >= 1 && Number(c[1]) <= CAPSTONE_ORDER.length) {
    const n = Number(c[1]);
    const type = CAPSTONE_ORDER[n - 1];
    const before = CAPSTONE_ORDER.slice(0, n - 1).filter((x) => x === type).length;
    return { id, n, type, dir: before % 2 ? -1 : 1, seed: 70000 + n * 15485, exam: false, capstone: true };
  }
  return null;
}
export const randomPracticeDay = (rand = Math.random) => `p${1 + Math.floor(rand() * PRACTICE_DAY_COUNT)}`;

// Minutes Chicago is behind UTC on a day (300 in summer, 360 in winter).
function chicagoOffset(day) {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', timeZoneName: 'shortOffset' })
    .formatToParts(new Date(`${day}T12:00:00Z`)).find((x) => x.type === 'timeZoneName').value;
  return -Number(part.replace('GMT', '')) * 60;
}

// One practice day's candles: { bars: [{ t, o, h, l, c, v }], type, dir, info }. `date`
// (YYYY-MM-DD) only sets the clock times on the chart.
export function practiceDay(id, { symbol = 'MX', date = '2026-01-05', calibration = null } = {}) {
  const info = practiceDayInfo(id);
  if (!info) return null;
  return makeDay(info, { symbol, date, calibration });
}

// The weekdays before `date` (YYYY-MM-DD), most recent first.
export function weekdaysBefore(date, n) {
  const out = [];
  const d = new Date(`${date}T12:00:00Z`);
  while (out.length < n) {
    d.setUTCDate(d.getUTCDate() - 1);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

// The `days` sessions before a practice day, for its chart's history (the levels traders mark
// before the open): each of a type picked from the day's seed, and moved so it closes a small gap
// away from where the next day opens. The same for everyone. Oldest candle first.
export function practiceHistory(id, { symbol = 'MX', date = '2026-01-05', calibration = null, days = 3, openPrice } = {}) {
  const info = practiceDayInfo(id);
  if (!info || days < 1) return [];
  const pick = rng(info.seed * 13 + 5);
  const tick = familyFor(symbol, calibration).tick;
  let nextOpen = openPrice !== undefined ? openPrice : makeDay(info, { symbol, date, calibration }).bars[0].o;
  const out = [];
  weekdaysBefore(date, days).forEach((d, k) => {
    const type = TYPE_IDS[Math.floor(pick() * TYPE_IDS.length)];
    const day = makeDay({ ...info, type, dir: pick() < 0.5 ? -1 : 1, seed: info.seed * 31 + (k + 1) * 7907 }, { symbol, date: d, calibration });
    const range = Math.max(...day.bars.map((b) => b.h)) - Math.min(...day.bars.map((b) => b.l));
    const close = nextOpen + (pick() - 0.5) * range * 0.3;
    const shift = Math.round((close - day.bars[day.bars.length - 1].c) / tick) * tick;
    const moved = day.bars.map((b) => ({ ...b, o: b.o + shift, h: b.h + shift, l: b.l + shift, c: b.c + shift }));
    out.unshift(...moved);
    nextOpen = moved[0].o;
  });
  return out;
}

function makeDay(info, { symbol, date, calibration }) {
  const fam = familyFor(symbol, calibration);
  const rand = rng(info.seed + (fam.key === 'NQ' ? 17 : 0));
  const r = (a, b) => a + (b - a) * rand();
  const type = DAY_TYPES[info.type];
  const { dir } = info;
  const p0 = fam.base * r(0.97, 1.03);
  const R = p0 * fam.dayRange * r(0.8, 1.25) * type.scale;

  // How much the price moves in each step inside a candle: the time of day, busy bursts, the news.
  const sd = [];
  const mood = r(0.85, 1.2); // some days are a little busier than others
  let burst = 0;
  for (let i = 0; i < MINUTES; i += 1) {
    burst = 0.9 * burst + 0.08 * gauss(rand);
    let size = fam.profile[Math.min(fam.profile.length - 1, Math.floor(i / BUCKET))] * p0 * mood * (type.calm || 1) * Math.exp(burst - 0.016);
    if (type.news && i >= type.news && i < type.news + 6) size *= i === type.news ? 4 : 2;
    // A 6-step walk spans about 1.35 of its step size over a candle; the high and low add the rest.
    for (let k = 0; k < SUB; k += 1) sd.push(size / 1.35 / Math.sqrt(SUB));
  }

  const path = [p0];
  const memo = {};
  const ctx = {
    R, memo, r,
    get last() { return path[path.length - 1]; },
    get o() { return path[Math.min(path.length - 1, OPEN * SUB)]; },
    get hi() { return Math.max(...path.slice(OPEN * SUB)); },
    get lo() { return Math.min(...path.slice(OPEN * SUB)); },
    get pmH() { return Math.max(...path.slice(0, OPEN * SUB + 1)); },
    get pmL() { return Math.min(...path.slice(0, OPEN * SUB + 1)); },
    or() {
      const part = path.slice(OPEN * SUB, (OPEN + 15) * SUB + 1);
      return { h: Math.max(...part), l: Math.min(...part) };
    },
    // Turns happen at levels: the nearest round number, premarket high or low, or opening range edge.
    snap(p) {
      const marks = [Math.round(p / fam.round) * fam.round, ctx.pmH, ctx.pmL];
      if (path.length > (OPEN + 15) * SUB) marks.push(ctx.or().h, ctx.or().l);
      const best = marks.reduce((a, b) => (Math.abs(b - p) < Math.abs(a - p) ? b : a));
      return Math.abs(best - p) <= R * 0.08 ? best : p;
    },
  };

  // Between two waypoints the price wanders (a random walk) and is pulled to the next one, more
  // of the pull coming where the market is busier.
  const walk = (toMinute, target) => {
    const from = path.length - 1;
    const to = toMinute * SUB;
    const start = path[from];
    let w = 0;
    let v = 0;
    const ws = [];
    const vs = [];
    for (let k = from; k < to; k += 1) {
      w += gauss(rand) * sd[k];
      v += sd[k] * sd[k];
      ws.push(w);
      vs.push(v);
    }
    for (let j = 0; j < ws.length; j += 1) {
      const f = vs[j] / v;
      path.push(start + ws[j] - f * w + f * (target - start));
    }
  };

  walk(OPEN, p0 + dir * R * r(-0.15, 0.2)); // the premarket
  for (const [minute, to] of type.steps(dir)) walk(minute, to(ctx));

  const tick = fam.tick;
  const round = (p) => Math.round(p / tick) * tick;
  const start = Date.parse(`${date}T00:00:00Z`) / 1000 + (7 * 60 + 30 + chicagoOffset(date)) * 60;
  const avg = (i) => fam.profile[Math.min(fam.profile.length - 1, Math.floor(i / BUCKET))] * p0;
  const bars = [];
  for (let i = 0; i < MINUTES; i += 1) {
    const part = path.slice(i * SUB, (i + 1) * SUB + 1);
    const o = round(part[0]);
    const c = round(part[part.length - 1]);
    const h = Math.max(o, c, round(Math.max(...part)));
    const l = Math.min(o, c, round(Math.min(...part)));
    const busy = Math.min(3, (h - l) / avg(i));
    bars.push({ t: start + i * 60, o, h, l, c, v: Math.round(fam.volume * 900 * (avg(i) / (p0 * fam.profile[16])) * (0.6 + 0.8 * rand()) * (0.6 + 0.5 * busy)) });
  }
  return { bars, type: info.type, dir, info };
}
